import {
  draggable,
  dropTargetForElements,
  monitorForElements,
} from "@atlaskit/pragmatic-drag-and-drop/element/adapter"
import { autoScrollForElements } from "@atlaskit/pragmatic-drag-and-drop-auto-scroll/element"
import {
  attachClosestEdge,
  extractClosestEdge,
} from "@atlaskit/pragmatic-drag-and-drop-hitbox/closest-edge"
import { reorderWithEdge } from "@atlaskit/pragmatic-drag-and-drop-hitbox/util/reorder-with-edge"
import { Controller } from "@hotwired/stimulus"
import { post } from "@rails/request.js"

// Vertically re-orderable list — the Hotwire twin of
// @a-safe-digital/react-draggable-list. Uses the SAME @atlaskit engine as the
// React source: per-item draggable (via a drag handle) + drop target with
// closest-edge detection, a monitor that reorders with `reorderWithEdge`, and
// container auto-scroll. On drop it dispatches `ui--draggable-list:reorder`
// ({ ids, oldIndex, newIndex }) and, when a reorder URL is set, POSTs the order.
//
// Items are wired via the `item` target lifecycle callbacks, so lists whose
// rows are added/removed at runtime (e.g. the action-builder) stay draggable
// without re-initialising the controller.
const ITEM_TYPE = "ui-draggable-list-item"

export default class extends Controller {
  static targets = ["list", "item", "handle"]
  static values = { reorderUrl: String }

  connect() {
    // `itemTargetConnected` can fire before `connect()`, so init lazily there too.
    this._itemCleanups ||= new Map()
    this._cleanups = [
      monitorForElements({
        canMonitor: ({ source }) => source.data.type === ITEM_TYPE,
        onDrop: ({ source, location }) => this._onDrop(source, location),
      }),
      autoScrollForElements({
        element: this.element,
        canScroll: ({ source }) => source.data.type === ITEM_TYPE,
        getConfiguration: () => ({ maxScrollSpeed: "fast" }),
      }),
    ]
  }

  disconnect() {
    this._cleanups.forEach((fn) => fn())
    this._cleanups = []
    this._itemCleanups.forEach((fn) => fn())
    this._itemCleanups.clear()
  }

  // Wire each row as it enters the DOM (fires for initial rows too), so
  // dynamically-added rows become draggable automatically.
  itemTargetConnected(item) {
    this._itemCleanups ||= new Map()
    if (this._itemCleanups.has(item)) return

    const handle = item.querySelector(
      "[data-ui--draggable-list-target='handle']",
    )

    const cleanupDraggable = draggable({
      element: item,
      dragHandle: handle || item,
      getInitialData: () => ({ type: ITEM_TYPE, itemId: item.dataset.itemId }),
      onDragStart: () => this._inner(item).classList.add("opacity-40"),
      onDrop: () => this._inner(item).classList.remove("opacity-40"),
    })

    const cleanupDropTarget = dropTargetForElements({
      element: item,
      getIsSticky: () => true,
      canDrop: ({ source }) => source.data.type === ITEM_TYPE,
      getData: ({ element, input }) =>
        attachClosestEdge(
          { type: ITEM_TYPE, itemId: item.dataset.itemId },
          { element, input, allowedEdges: ["top", "bottom"] },
        ),
      onDragEnter: ({ source, self }) => {
        if (source.element === item) return
        this._paintEdge(item, extractClosestEdge(self.data))
      },
      onDrag: ({ source, self }) => {
        if (source.element === item) return
        this._paintEdge(item, extractClosestEdge(self.data))
      },
      onDragLeave: () => this._clearEdge(item),
      onDrop: () => this._clearEdge(item),
    })

    this._itemCleanups.set(item, () => {
      cleanupDraggable()
      cleanupDropTarget()
    })
  }

  itemTargetDisconnected(item) {
    const cleanup = this._itemCleanups.get(item)
    if (cleanup) {
      cleanup()
      this._itemCleanups.delete(item)
    }
  }

  _onDrop(source, location) {
    const target = location.current.dropTargets[0]
    if (!target) return

    const items = this.itemTargets
    // Match by element reference (robust when rows lack a stable data-item-id).
    const startIndex = items.indexOf(source.element)
    const indexOfTarget = items.indexOf(target.element)
    if (
      startIndex === -1 ||
      indexOfTarget === -1 ||
      startIndex === indexOfTarget
    ) {
      this._clearEdge(target.element)
      return
    }

    const closestEdgeOfTarget = extractClosestEdge(target.data)
    const reordered = reorderWithEdge({
      axis: "vertical",
      list: items,
      startIndex,
      indexOfTarget,
      closestEdgeOfTarget,
    })

    // Apply the new order to the DOM.
    const list = this.listTarget
    reordered.forEach((el) => list.appendChild(el))
    this._clearEdge(target.element)

    const ids = reordered.map((el) => el.dataset.itemId)
    const newIndex = reordered.indexOf(source.element)

    this.dispatch("reorder", {
      detail: { ids, oldIndex: startIndex, newIndex },
    })
    this._persist(ids)
  }

  async _persist(ids) {
    if (!this.reorderUrlValue) return
    try {
      await post(this.reorderUrlValue, {
        body: { ids },
        contentType: "application/json",
        responseKind: "json",
      })
    } catch (e) {
      console.error("[ui--draggable-list] Failed to persist order:", e)
    }
  }

  // The row's inner wrapper carries the dragging/drop-edge styling. Falls back
  // to the row itself for consumers (e.g. action cards) with no inner element.
  _inner(item) {
    return (
      item.querySelector("[data-ui--draggable-list-target='inner']") || item
    )
  }

  _paintEdge(item, edge) {
    if (!edge) return
    const inner = this._inner(item)
    inner.classList.remove("border-t-2", "border-b-2", "border-primary")
    inner.classList.add(
      edge === "top" ? "border-t-2" : "border-b-2",
      "border-primary",
    )
  }

  _clearEdge(item) {
    this._inner(item).classList.remove(
      "border-t-2",
      "border-b-2",
      "border-primary",
    )
  }
}
